'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from '@/components/InfiniteScroll';
import { EPlatform } from '@/utils/enums/platform-enum';
import { MarketingRuntimeConfig, SeedingItem } from '@/app/(activity)/campaigns/[slug]/types';
import SeedingItemCard from './SeedingItemCard';
import SeedingItemDialog from './SeedingItemDialog';
import { useSeedingItems } from '../hooks/useSeedingItems';
import { useSeedingItemDetail } from '../hooks/useSeedingItemDetail';
import { useSeedingTracking } from '../hooks/useSeedingTracking';
import { getSeedingItemKey } from '../utils/getSeedingItemKey';
import { splitItemsIntoColumns } from '../utils/splitItemsIntoColumns';

const MIN_SEEDING_COLUMN_COUNT = 2;
const MAX_SEEDING_COLUMN_COUNT = 5;
const MIN_SEEDING_CARD_WIDTH = 320;
const PC_SEEDING_COLUMN_GAP = 16;
const WAP_SEEDING_COLUMN_GAP = 11;
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface SeedingItemListProps {
  marketingPageId: number;
  isPreview: boolean;
  deviceType: number;
  runtimeConfig: MarketingRuntimeConfig;
  initialData?: {
    items: SeedingItem[];
    total: number;
    has_more: boolean;
  } | null;
  isAppCommunicationReady?: boolean;
  /** 种草列表全部分页加载完成回调（用于控制底部推荐版位和FAQ的展示时序） */
  onSeedingFullyLoaded?: () => void;
  adp?: string;
}

function getResponsiveSeedingColumnCount(containerWidth: number, columnGap: number): number {
  if (containerWidth <= 0) return MAX_SEEDING_COLUMN_COUNT;

  for (let count = MAX_SEEDING_COLUMN_COUNT; count > MIN_SEEDING_COLUMN_COUNT; count -= 1) {
    const cardWidth = (containerWidth - columnGap * (count - 1)) / count;
    if (cardWidth >= MIN_SEEDING_CARD_WIDTH) {
      return count;
    }
  }

  return MIN_SEEDING_COLUMN_COUNT;
}

export default function SeedingItemList({
  marketingPageId,
  isPreview,
  deviceType,
  runtimeConfig,
  initialData,
  isAppCommunicationReady = true,
  onSeedingFullyLoaded,
  adp,
}: SeedingItemListProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const columnGap = isWap ? WAP_SEEDING_COLUMN_GAP : PC_SEEDING_COLUMN_GAP;
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const [columnCount, setColumnCount] = useState(
    isWap ? MIN_SEEDING_COLUMN_COUNT : MAX_SEEDING_COLUMN_COUNT,
  );
  const fullyLoadedRef = useRef(false);

  const {
    trackProductClick,
    trackProductExposure,
    trackCampaignPopupExp,
    trackCampaignPopupCollectClick,
    trackCampaignPopupClick,
  } = useSeedingTracking(runtimeConfig);

  const {
    items,
    loading,
    hasMore,
    page,
    total,
    loadMore,
    isReachingEnd,
    isValidating,
    hasAttemptedFetch,
  } = useSeedingItems({
    marketingPageId,
    isPreview,
    initialData,
    adp,
  });

  useEffect(() => {
    if (!isAppCommunicationReady || fullyLoadedRef.current) return;
    if (!hasAttemptedFetch) return;
    if (!isReachingEnd || isValidating) return;

    fullyLoadedRef.current = true;
    onSeedingFullyLoaded?.();
  }, [
    isAppCommunicationReady,
    hasAttemptedFetch,
    isReachingEnd,
    isValidating,
    onSeedingFullyLoaded,
  ]);

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>(undefined);
  const {
    detail,
    loading: detailLoading,
    fetchDetail,
    reset: resetDetail,
  } = useSeedingItemDetail({
    marketingPageId,
    isPreview,
  });

  const handleItemClick = useCallback(
    async (item: SeedingItem, index: number) => {
      trackProductClick(item, index);
      setSelectedItemIndex(index);
      setSelectedItemId(item.item_id);
      await fetchDetail(item.item_id);
      trackCampaignPopupExp(index);
    },
    [fetchDetail, trackProductClick, trackCampaignPopupExp],
  );

  const handleDialogClose = useCallback(() => {
    setSelectedItemId(null);
    resetDetail();
    setSelectedItemIndex(undefined);
  }, [resetDetail]);

  const seedingDialogProps = {
    open: selectedItemId !== null,
    detail,
    loading: detailLoading,
    onClose: handleDialogClose,
    index: selectedItemIndex ?? 0,
    trackCampaignPopupCollectClick,
    trackCampaignPopupClick,
  };

  const columns = useMemo(() => splitItemsIntoColumns(items, columnCount), [items, columnCount]);

  const updateColumnCount = useCallback(() => {
    const target = columnsRef.current;
    if (!target) return;

    const nextColumnCount = getResponsiveSeedingColumnCount(target.clientWidth, columnGap);
    setColumnCount((current) => (current === nextColumnCount ? current : nextColumnCount));
  }, [columnGap]);

  const cancelScheduledColumnCountUpdate = useCallback(() => {
    if (resizeFrameRef.current === null || typeof window === 'undefined') return;
    window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = null;
  }, []);

  const scheduleColumnCountUpdate = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      updateColumnCount();
      return;
    }

    cancelScheduledColumnCountUpdate();
    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      updateColumnCount();
    });
  }, [cancelScheduledColumnCountUpdate, updateColumnCount]);

  const setColumnsElement = useCallback(
    (node: HTMLDivElement | null) => {
      columnsRef.current = node;
      if (node) {
        scheduleColumnCountUpdate();
      }
    },
    [scheduleColumnCountUpdate],
  );

  useIsomorphicLayoutEffect(() => {
    const target = columnsRef.current;
    if (!target) return;

    updateColumnCount();
    scheduleColumnCountUpdate();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleColumnCountUpdate);
      return () => {
        window.removeEventListener('resize', scheduleColumnCountUpdate);
        cancelScheduledColumnCountUpdate();
      };
    }

    const resizeObserver = new ResizeObserver(updateColumnCount);
    resizeObserver.observe(target);
    window.addEventListener('resize', scheduleColumnCountUpdate);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleColumnCountUpdate);
      cancelScheduledColumnCountUpdate();
    };
  }, [
    cancelScheduledColumnCountUpdate,
    items.length,
    scheduleColumnCountUpdate,
    updateColumnCount,
  ]);

  if (!isAppCommunicationReady) {
    return null;
  }

  return (
    <div className={`w-full ${isWap ? 'px-[12px] py-[12px]' : 'px-[48px] pt-[24px]'}`}>
      {items.length === 0 ? (
        <div className="min-h-[200px] pb-[64px]" />
      ) : (
        <div className="w-full pb-[64px]">
          <div
            ref={setColumnsElement}
            className={`flex w-full items-start transition-[gap] duration-200 ease-out motion-reduce:transition-none ${isWap ? 'gap-x-[11px]' : 'gap-x-[16px]'}`}
          >
            {columns.map((columnItems, colIndex) => (
              <div
                key={`seeding-col-${colIndex}`}
                className={`flex min-w-0 flex-1 flex-col ${isWap ? 'gap-y-[20px]' : 'gap-y-[24px]'}`}
              >
                {columnItems.map(({ item, index }) => (
                  <SeedingItemCard
                    key={getSeedingItemKey(item, index)}
                    item={item}
                    index={index}
                    isWap={isWap}
                    columnCount={columnCount}
                    onClick={() => handleItemClick(item, index)}
                    onExposure={() => trackProductExposure(item, index)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {hasMore && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadMore}
          page={page}
          total={total}
          limit={20}
        />
      )}

      <SeedingItemDialog {...seedingDialogProps} isWap={isWap} />
    </div>
  );
}
